// Human labels and badge colours for the contract's enums.
//
// Every one is an exhaustive `switch` closed by `assertNever`, so adding a
// value to a `@crm/shared` enum breaks the build here instead of rendering an
// unlabelled cell. A lookup object keyed by the union would type-check too, but
// it can't express the "lost is not a stage" and "sent/paid mean money moved"
// distinctions the badge colours below encode.

import type { BillingContactRole, Cadence, DealState, InvoiceRecipient } from "@crm/shared";
import type { ChargeStatus, CostBillingMode, MilestoneState } from "@crm/shared";
import type { BillingTiming, PriceSource, ShareKind, TermLineKind } from "@crm/shared";
import type { FactSource, FigureSource, MeterSource, UnpricedReason } from "@crm/shared";
import type { MeterUnit } from "@crm/shared";
import type { CalendarEventKind, ImplFeeUnresolvedReason, SplitSource } from "@crm/shared";
import { assertNever } from "../lib/assertNever.js";

/** The subset of Fluent's Badge appearances/colours this app uses. */
export type BadgeColor =
  | "brand"
  | "danger"
  | "important"
  | "informative"
  | "severe"
  | "success"
  | "subtle"
  | "warning";

export function dealStateLabel(state: DealState): string {
  switch (state) {
    case "lead":
      return "Lead";
    case "qualified":
      return "Qualified";
    case "demoed":
      return "Demoed";
    case "proposal":
      return "Proposal";
    case "decision_maker_bought_in":
      return "Decision Maker Bought-In";
    case "won":
      return "Won";
    case "implementation":
      return "Implementation";
    case "live":
      return "Live";
    case "ended":
      return "Ended";
    case "lost":
      return "Lost";
    default:
      return assertNever(state, "deal state");
  }
}

/**
 * Colour carries the one distinction a reader scans for: is this deal still a
 * forecast, is it contracted, or is it dead. The linear stages stay neutral so
 * the two terminal outcomes are the only things that draw the eye. An ended
 * deal was won and is over, so it goes quiet rather than reading as either.
 */
export function dealStateColor(state: DealState): BadgeColor {
  switch (state) {
    case "lead":
    case "qualified":
    case "demoed":
      return "subtle";
    case "proposal":
    case "decision_maker_bought_in":
      return "informative";
    case "won":
    case "implementation":
    case "live":
      return "success";
    case "ended":
      return "subtle";
    case "lost":
      return "danger";
    default:
      return assertNever(state, "deal state");
  }
}

/** A line we sell, or a cost we spent and recharge a share of. */
export function termLineKindLabel(kind: TermLineKind): string {
  switch (kind) {
    case "revenue":
      return "Revenue";
    case "pass_through_cost":
      return "Pass-through cost";
    default:
      return assertNever(kind, "term line kind");
  }
}

/** Which fact table a line's selector reads. */
export function meterSourceLabel(source: MeterSource): string {
  switch (source) {
    case "transcripts":
      return "transcripts";
    case "usage":
      return "usage facts";
    case "provider_cost":
      return "provider cost facts";
    case "deal":
      return "deal facts";
    default:
      return assertNever(source, "term line source");
  }
}

/** Where the money comes from: the paper, the calendar, or the facts. */
export function priceSourceLabel(price: PriceSource): string {
  switch (price) {
    case "contract_rate":
      return "Contract rate";
    case "flat_per_period":
      return "Flat per period";
    case "fact_amount":
      return "The facts state the money";
    default:
      return assertNever(price, "price source");
  }
}

/** Which agreement percentage governs a line's share. */
export function shareKindLabel(kind: ShareKind): string {
  switch (kind) {
    case "implementation":
      return "Implementation split";
    case "recurring":
      return "Recurring split";
    case "cost":
      return "Cost split";
    default:
      return assertNever(kind, "share kind");
  }
}

/** Whether a bill covers the period it falls in or the one before. */
export function billingTimingLabel(timing: BillingTiming): string {
  switch (timing) {
    case "arrears":
      return "in arrears";
    case "advance":
      return "in advance";
    default:
      return assertNever(timing, "billing timing");
  }
}

export function cadenceLabel(cadence: Cadence): string {
  switch (cadence) {
    case "one_time":
      return "One time";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "annual":
      return "Annual";
    case "milestone":
      return "Milestones";
    default:
      return assertNever(cadence, "cadence");
  }
}

/** Where a charge has got to: still open, ready to bill, on a document, paid. */
export function chargeStatusLabel(status: ChargeStatus): string {
  switch (status) {
    case "open":
      return "Open";
    case "ready":
      return "Ready";
    case "invoiced":
      return "Invoiced";
    case "paid":
      return "Paid";
    default:
      return assertNever(status, "charge status");
  }
}

/** `invoiced`/`paid` are the statuses where a document left the building. */
export function chargeStatusColor(status: ChargeStatus): BadgeColor {
  switch (status) {
    case "open":
      return "subtle";
    case "ready":
      return "warning";
    case "invoiced":
      return "informative";
    case "paid":
      return "success";
    default:
      return assertNever(status, "charge status");
  }
}

/** A milestone is planned, the deliverable landed, or the money went out. */
export function milestoneStateLabel(state: MilestoneState): string {
  switch (state) {
    case "billed":
      return "Billed";
    case "reached":
      return "Reached";
    case "planned":
      return "Planned";
    default:
      return assertNever(state, "milestone state");
  }
}

/**
 * WHERE a bill's revenue share came from (migration 0013, widened by 0014).
 * Named for the answer rather than for the column, because the question a reader
 * is asking of this word is "can this number move" — and each source answers it
 * differently: an override was typed by a person, a frozen split is what an
 * invoice already said, a line or deal override is a standing commercial
 * decision, and an agreement split is a forecast that re-derives itself the day
 * the terms change.
 */
export function splitSourceLabel(source: SplitSource): string {
  switch (source) {
    case "override":
      return "Override";
    case "frozen":
      return "Frozen";
    case "line":
      return "From this line";
    case "deal":
      return "From this deal";
    case "agreement":
      return "From the agreement";
    case "unresolved":
      return "Unresolved";
    default:
      return assertNever(source, "split source");
  }
}

/**
 * `unresolved` is the only one that is a PROBLEM, so it is the only one drawn as
 * a warning. `agreement` is the ordinary state of every unbilled row and gets
 * the quietest tone there is — a table where the normal case is decorated is a
 * table where the abnormal one stops standing out.
 *
 * `line` and `deal` share the override's tone rather than the agreement's, and
 * that is the honest grouping: all three are numbers a person chose, and the
 * distinction a reader cares about is chosen-by-someone versus derived-from-paper.
 */
export function splitSourceColor(source: SplitSource): BadgeColor {
  switch (source) {
    case "override":
    case "line":
    case "deal":
      return "brand";
    case "frozen":
      return "subtle";
    case "agreement":
      return "informative";
    case "unresolved":
      return "warning";
    default:
      return assertNever(source, "split source");
  }
}

/**
 * The sentence behind the label: what this source means for the number beside
 * it, and — for the one that has no number — what would fix it.
 *
 * One wording, used by the schedule table's tooltips and by the item editor's
 * disabled fields, so the table and the form cannot explain the same row two
 * different ways.
 */
export function splitSourceReason(source: SplitSource): string {
  switch (source) {
    case "override":
      return "Overridden on this one charge. An override outranks both what the charge froze when it went out and the agreement's term, so this is the percentage that applies until somebody clears it.";
    case "frozen":
      return "Frozen when this charge went out. A renegotiated agreement cannot restate an invoice that has already been sent — a correction is an override, which stays visible for what it is.";
    case "line":
      return "From this term line's own share override — an exception negotiated for this one clause, which applies to every period the clause bills and outranks the agreement.";
    case "deal":
      return "From this deal's split override, which applies to every clause under it. This is how a deal that carries no revenue share is stated once rather than copied onto each clause; it outranks the agreement and is outranked by a clause's own override.";
    case "agreement":
      return "Resolved from the governing agreement's term in force on the first day of the period, and resolved again on every read until an invoice freezes it. A new term re-prices this forecast on its own.";
    case "unresolved":
      return "No override, nothing frozen, and no agreement term covers this due date — so nobody can say what share of this bill is ours. Add a term to the governing agreement, or set an override on the bill.";
    default:
      return assertNever(source, "split source");
  }
}

/**
 * WHAT A BILL IS FOR, in one word.
 *
 * OPEN, unlike every other function in this file, and deliberately: a charge
 * type is a term line's own word for what it sells, carried on the wire as a
 * plain string, and the business grows a new one whenever it sells something
 * new. A value this build has never heard of is one the database has already
 * accepted, so it is rendered as itself — `professional_services` reads as
 * "Professional services" — rather than crashing a row or, worse, being dropped
 * from it.
 */
export function chargeTypeLabel(value: string): string {
  return SPELLED.get(value) ?? sentenceCase(value);
}

/**
 * The handful of charge types whose written form is not their sentence case.
 *
 * An acronym is the only thing sentence-casing gets wrong, and it gets it
 * wrong visibly: "Saas" on an invoice reads as a typo. A MAP rather than a
 * switch on purpose — this is a spelling list, not a vocabulary, so a value
 * missing from it renders as itself and nothing breaks.
 */
const SPELLED = new Map<string, string>([
  ["saas", "SaaS"],
  ["ai_usage", "AI usage"],
]);

/** `professional_services` → `Professional services`. The last-resort rendering. */
function sentenceCase(value: string): string {
  return value.replace(/_/g, " ").replace(/^./, (first) => first.toUpperCase());
}

/**
 * Phrased as what happens rather than as the enum value, because the enum value
 * alone reads like a property of the cost when it is a decision about the
 * invoice.
 */
export function costBillingModeLabel(mode: CostBillingMode): string {
  switch (mode) {
    case "bundled":
      return "Bundled with the regular bill";
    case "separate":
      return "Separate cost-sharing bill";
    default:
      return assertNever(mode, "cost billing mode");
  }
}

/**
 * Named for what LANDS on the day rather than for the table it came from —
 * "Terms change", not "Agreement term". A calendar is read as a list of things
 * that happen, and the row's provenance is the one thing the reader already has
 * from the screen they are on.
 *
 * The badge TONE for each kind is deliberately not here: seven kinds need seven
 * distinct tones, `Tone` lives in `design/Badge.tsx`, and that module imports
 * `BadgeColor` from this one — so putting the mapping here would make the two
 * files import each other. It sits in `pages/calendar/calendarEvents.ts`.
 */
export function calendarEventKindLabel(kind: CalendarEventKind): string {
  switch (kind) {
    case "bill_due":
      return "Bill due";
    case "cost_bill_due":
      return "Cost recovery";
    case "term_change":
      return "Terms change";
    case "impl_fee_due":
      return "Implementation fee";
    case "term_line_change":
      return "Price change";
    case "deal_start":
      return "Deal starts";
    case "deal_renewal":
      return "Renewal";
    default:
      return assertNever(kind, "calendar event kind");
  }
}

/**
 * Phrased as the thing somebody has to go and do, because that is what an
 * unresolved implementation fee is: a worklist entry. The enum names the first
 * step of the resolution that failed (see the implementation-fee report), and
 * each of these is a different person's job.
 */
export function implFeeUnresolvedReasonLabel(reason: ImplFeeUnresolvedReason): string {
  switch (reason) {
    case "no_account_count":
      return "No customer account count recorded";
    case "no_agreement":
      return "No agreement governs this deal";
    case "no_term":
      return "No term in force at the deal's start";
    case "no_fee_schedule":
      return "The term prices no implementation fee";
    case "no_tier_match":
      return "No band covers a customer this large";
    case "currency_mismatch":
      // Nothing here converts. A suggestion in another currency is reported
      // rather than restated, because a converted fee states a rate as a fact
      // and it is not one.
      return "The band is priced in another currency, and nothing is converted";
    default:
      return assertNever(reason, "implementation fee unresolved reason");
  }
}

/**
 * Where a line's figure came from, in words a reader can act on.
 *
 * The five arms are not degrees of confidence — every one of them is a real
 * figure — they say WHO said so, which is what a person checks a figure
 * against. `none` is the one that is not a figure at all: the line reports what
 * it could not price instead.
 */
export function figureSourceLabel(source: FigureSource): string {
  switch (source) {
    case "contract":
      return "the contract";
    case "deal_fact":
      return "a recorded fact";
    case "provider_cost":
      return "the provider's own figure";
    case "rate_card":
      return "our rate card";
    case "none":
      return "nothing priced it";
    default:
      return assertNever(source, "figure source");
  }
}

/** Who said so. `person` is somebody through the SPA or the CLI. */
export function factSourceLabel(source: FactSource): string {
  switch (source) {
    case "person":
      return "A person";
    case "import":
      return "A bulk load of history";
    case "platform_deployment":
      return "The rollout tracker";
    case "deployment_backend":
      return "A deployment backend";
    case "bank_of_canada":
      return "The Bank of Canada";
    case "provider_api":
      return "A provider's rate card";
    case "openai_api":
      return "OpenAI";
    case "anthropic_api":
      return "Anthropic";
    case "twilio_api":
      return "Twilio";
    case "aws_ce":
      return "AWS Cost Explorer";
    default:
      return assertNever(source, "fact source");
  }
}

/**
 * Why a fact carried no figure, phrased as the thing that is missing.
 *
 * Every one of these is COUNTED rather than dropped, so a line short of its
 * figure says by how much and why. `excluded` is the one that is not a
 * problem: the selector refused the fact on purpose, and saying so out loud is
 * how "we do not bill auto-transfers" stays a visible figure.
 */
export function unpricedReasonLabel(reason: UnpricedReason): string {
  switch (reason) {
    case "null_field":
      return "a field the rule reads was empty";
    case "rate_unavailable":
      return "no rate covers it";
    case "division_by_zero":
      return "the rule divided by zero";
    case "excluded":
      return "the selector refuses it";
    case "rule_missing":
      return "nobody has written the quantity rule";
    default:
      return assertNever(reason, "unpriced reason");
  }
}

/**
 * The same reasons, short enough to ride after a count inside a table cell.
 *
 * `unpricedReasonLabel` explains the reason to a reader who met it once. This
 * form goes on a row a reader SCANS, where "290 skipped for an empty field"
 * has to fit beside the figure it is short of. Both come off the same enum, so
 * a reason added to the contract breaks the build in two places rather than
 * losing its count in one of them.
 */
export function unpricedReasonShort(reason: UnpricedReason): string {
  switch (reason) {
    case "null_field":
      return "skipped for an empty field";
    case "rate_unavailable":
      return "skipped for no rate";
    case "division_by_zero":
      return "skipped for a zero divisor";
    case "excluded":
      return "excluded by the rule";
    case "rule_missing":
      return "skipped for no quantity rule";
    default:
      return assertNever(reason, "unpriced reason");
  }
}

/**
 * The arm that supplied a UNIT PRICE, in the words that follow a count.
 *
 * "315,875 audio seconds × rate card" is the whole reading, so the arm is
 * named as a price rather than as a sentence: `figureSourceLabel` answers
 * "who said so" about a FIGURE, and its words do not fit after a multiply
 * sign. `none` cannot price a counted line, and says so rather than pretending
 * to a price.
 */
export function figureSourcePriceWord(source: FigureSource): string {
  switch (source) {
    case "contract":
      return "the contract";
    case "deal_fact":
      return "a recorded fact";
    case "provider_cost":
      return "the provider's figure";
    case "rate_card":
      return "rate card";
    case "none":
      return "no price yet";
    default:
      return assertNever(source, "figure source");
  }
}

/**
 * A meter unit as the word that follows a count: `audio_second` and 315,875
 * make "audio seconds".
 *
 * A table that prints the enum member prints `audio_second` beside a count of
 * three hundred thousand, which reads as a column name rather than as a
 * quantity. Keyed by the union rather than switched, so a unit added to the
 * contract is a missing key here — a compile error, not an untranslated word.
 */
const UNIT_WORDS: Record<MeterUnit, { one: string; many: string }> = {
  minute: { one: "minute", many: "minutes" },
  call: { one: "call", many: "calls" },
  usage: { one: "use", many: "uses" },
  message: { one: "message", many: "messages" },
  period: { one: "period", many: "periods" },
  input_token: { one: "input token", many: "input tokens" },
  output_token: { one: "output token", many: "output tokens" },
  cached_input_token: { one: "cached input token", many: "cached input tokens" },
  cache_creation_input_token: {
    one: "cache creation input token",
    many: "cache creation input tokens",
  },
  reasoning_token: { one: "reasoning token", many: "reasoning tokens" },
  audio_input_token: { one: "audio input token", many: "audio input tokens" },
  audio_output_token: { one: "audio output token", many: "audio output tokens" },
  audio_second: { one: "audio second", many: "audio seconds" },
  character: { one: "character", many: "characters" },
  carrier_minute: { one: "carrier minute", many: "carrier minutes" },
  currency_minor: { one: "minor unit", many: "minor units" },
};

export function meterUnitPhrase(unit: MeterUnit, quantity: number): string {
  const words = UNIT_WORDS[unit];
  return quantity === 1 ? words.one : words.many;
}

/**
 * What a job is called in a cell: the first eight characters of its id.
 *
 * Enough to compare two of them by eye and to quote at a colleague, and short
 * enough to ride beside a figure. Stated once here because more than one screen
 * names a job, and two of them would otherwise cut it at a different length.
 */
export function shortRunId(runId: string): string {
  return runId.slice(0, 8);
}

export function invoiceRecipientLabel(recipient: InvoiceRecipient): string {
  switch (recipient) {
    case "partner":
      return "Invoice the partner";
    case "customer":
      return "Invoice the customer";
    default:
      return assertNever(recipient, "invoice recipient");
  }
}

/** What a billing contact is to an invoice. */
export function billingContactRoleLabel(role: BillingContactRole): string {
  switch (role) {
    case "recipient":
      return "Receives invoices";
    case "approver":
      return "Approves invoices";
    default:
      return assertNever(role, "billing contact role");
  }
}
